import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { CheckCircle, XCircle, Clock, Coffee, Download, Sun, Sunset, SunMedium, FileText, Square, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import { generatePresenceCollectivePDF, downloadPDF } from '../utils/pdfGenerator';

const Presence = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [demiJournee, setDemiJournee] = useState('matin'); // 'matin' | 'apres_midi' | 'journee'
  const [presences, setPresences] = useState({});
  const [signatures, setSignatures] = useState({});
  const [formateurSignature, setFormateurSignature] = useState(null);
  const [alreadyValidated, setAlreadyValidated] = useState([]);
  const [selectedStagiaires, setSelectedStagiaires] = useState({});

  useEffect(() => {
    loadStagiaires();
    loadFormateurSignature();
  }, []);

  useEffect(() => {
    // Vérifier les présences existantes quand la date ou la demi-journée change
    checkExistingPresences();
  }, [date, demiJournee]);

  const checkExistingPresences = async () => {
    try {
      const response = await axios.get(`/presences/date/${date}`);
      if (demiJournee === 'journee') {
        // Pour "jour entier", un stagiaire est considéré déjà validé seulement
        // si matin ET après-midi sont enregistrés.
        const matinIds = response.data.filter(p => p.periode === 'matin').map(p => p.stagiaire_id);
        const apmIds = response.data.filter(p => p.periode === 'apres_midi').map(p => p.stagiaire_id);
        setAlreadyValidated(matinIds.filter(id => apmIds.includes(id)));
      } else {
        const validated = response.data
          .filter(p => p.periode === demiJournee)
          .map(p => p.stagiaire_id);
        setAlreadyValidated(validated);
      }
    } catch (error) {
      setAlreadyValidated([]);
    }
  };

  const loadStagiaires = async () => {
    try {
      const response = await axios.get('/stagiaires');
      setStagiaires(response.data);
      
      const initialPresences = {};
      response.data.forEach((s) => {
        initialPresences[s.user_id] = 'present';
      });
      setPresences(initialPresences);
      
      // Load signatures for all stagiaires
      if (response.data.length > 0) {
        const userIds = response.data.map(s => s.user_id).join(',');
        try {
          const sigResponse = await axios.get(`/signatures/bulk?user_ids=${userIds}`);
          setSignatures(sigResponse.data);
        } catch (err) {
        }
      }
    } catch (error) {
    }
  };

  const loadFormateurSignature = async () => {
    if (user.role === 'formateur' || user.role === 'tuteur') {
      try {
        const response = await axios.get(`/signatures/${user.user_id}`);
        setFormateurSignature(response.data.signature);
      } catch (error) {
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Filtrer uniquement les stagiaires sélectionnés
    const selectedIds = Object.keys(selectedStagiaires).filter(id => selectedStagiaires[id]);
    if (selectedIds.length === 0) {
      toast.error('Veuillez sélectionner au moins un stagiaire');
      return;
    }

    try {
      // Vérifier quels stagiaires n'ont pas encore de présence
      const existingPresences = await axios.get(`/presences/date/${date}`);

      // Périodes à enregistrer : ['matin','apres_midi'] pour "jour entier", sinon une seule.
      const periodesToSubmit = demiJournee === 'journee'
        ? ['matin', 'apres_midi']
        : [demiJournee];

      // Construire un set des (stagiaire_id, periode) déjà validés
      const validatedSet = new Set(
        existingPresences.data.map(p => `${p.stagiaire_id}::${p.periode}`)
      );

      // Construire les requêtes : 1 par (stagiaire × période) à enregistrer (sans doublon)
      const requests = [];
      const stagiairesTouched = new Set();
      stagiaires.forEach(s => {
        if (!selectedIds.includes(s.user_id)) return;
        periodesToSubmit.forEach(periode => {
          if (validatedSet.has(`${s.user_id}::${periode}`)) return; // déjà validé pour cette période
          requests.push(
            axios.post('/presences', {
              stagiaire_id: s.user_id,
              formateur_id: user.role === 'formateur' ? user.user_id : null,
              tuteur_id: user.role === 'tuteur' ? user.user_id : null,
              date,
              periode,
              statut: presences[s.user_id],
              lieu: user.role === 'tuteur' ? 'entreprise' : 'centre',
              type_feuille: 'collective',
              signature_stagiaire: signatures[s.user_id] || null,
              signature_formateur: formateurSignature || null
            })
          );
          stagiairesTouched.add(s.user_id);
        });
      });

      if (requests.length === 0) {
        toast.info('Tous les stagiaires sélectionnés ont déjà une présence enregistrée.');
        return;
      }

      await Promise.all(requests);
      checkExistingPresences();

      // Décocher les stagiaires soumis
      const newSelected = { ...selectedStagiaires };
      stagiairesTouched.forEach(uid => { newSelected[uid] = false; });
      setSelectedStagiaires(newSelected);

      const periodeLabel =
        demiJournee === 'matin' ? 'Matin'
        : demiJournee === 'apres_midi' ? 'Après-midi'
        : 'Jour entier';
      toast.success(`Présence ${periodeLabel} enregistrée pour ${stagiairesTouched.size} stagiaire(s)`);

      // Envoyer automatiquement le récap à la direction
      try {
        await axios.post('/presences/envoyer-recap-direction', { date });
        toast.success('Récapitulatif envoyé à la direction');
      } catch (recapErr) {
        const detail = recapErr.response?.data?.detail || "Erreur inconnue";
        toast.error(`Présences enregistrées mais erreur lors de l'envoi du récap: ${detail}`);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('Certaines présences existent déjà.');
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    }
  };

  const handleGeneratePDF = async () => {
    try {
      // Récupérer toutes les présences enregistrées du jour pour le PDF cumulatif
      const existingRes = await axios.get(`/presences/date/${date}`);
      const allDayPresences = existingRes.data || [];
      
      // Construire les données par stagiaire
      const stagiaireMap = {};
      stagiaires.forEach(s => {
        const uid = s.user_id;
        stagiaireMap[uid] = {
          nom: s.user_info?.nom || '',
          prenom: s.user_info?.prenom || '',
          centre: s.centre_formation || '',
          user_id: uid,
          matin: null,
          apres_midi: null
        };
      });
      
      // Remplir depuis les présences enregistrées en base
      const formateursMap = {};
      allDayPresences.forEach(p => {
        const sid = p.stagiaire_id;
        if (!stagiaireMap[sid]) return;
        
        const periode = p.periode;
        if (periode === 'matin' || periode === 'apres_midi') {
          stagiaireMap[sid][periode] = {
            statut: p.statut || 'present',
            signature: p.signature_stagiaire || signatures[sid] || null
          };
        }
        
        // Collecter les formateurs
        if (p.formateur_id && !formateursMap[p.formateur_id]) {
          formateursMap[p.formateur_id] = {
            id: p.formateur_id,
            signature: p.signature_formateur || null
          };
        }
      });
      
      // Compléter avec les données en cours (non encore enregistrées) pour la période active
      stagiaires.forEach(s => {
        const uid = s.user_id;
        if (stagiaireMap[uid] && !stagiaireMap[uid][demiJournee]) {
          if (selectedStagiaires[uid]) {
            stagiaireMap[uid][demiJournee] = {
              statut: presences[uid] || 'present',
              signature: signatures[uid] || null
            };
          }
        }
      });
      
      // Déterminer si c'est matin seul ou cumulé
      const hasMatin = Object.values(stagiaireMap).some(s => s.matin);
      const hasApresMidi = Object.values(stagiaireMap).some(s => s.apres_midi);
      let periode = 'matin';
      if (hasMatin && hasApresMidi) periode = 'journee';
      else if (hasApresMidi && !hasMatin) periode = 'apres_midi';
      
      // Construire la liste des formateurs avec signatures
      const formateursList = [];
      
      // Signature du formateur courant : profil OU depuis les présences du jour
      let currentFormateurSig = formateurSignature || null;
      if (!currentFormateurSig && formateursMap[user.user_id]) {
        currentFormateurSig = formateursMap[user.user_id].signature || null;
      }
      
      if (user.role === 'formateur' || user.role === 'tuteur') {
        formateursList.push({
          nom: user.nom || '',
          prenom: user.prenom || '',
          role: user.role,
          signature: currentFormateurSig
        });
      }
      
      // Ajouter les autres formateurs trouvés dans les présences du jour
      for (const fid of Object.keys(formateursMap)) {
        if (fid !== user.user_id) {
          // Essayer de récupérer le nom du formateur depuis les stagiaires enrichis ou un appel
          let fNom = 'Formateur', fPrenom = '';
          try {
            const sigRes = await axios.get(`/signatures/${fid}`).catch(() => null);
            const extraSig = sigRes?.data?.signature || formateursMap[fid].signature || null;
            // Chercher le nom du formateur dans les utilisateurs
            const userRes = await axios.get(`/formateurs`);
            const fUser = (userRes?.data || []).find(f => f.user_id === fid);
            if (fUser) { fNom = fUser.nom || 'Formateur'; fPrenom = fUser.prenom || ''; }
            formateursList.push({
              nom: fNom, prenom: fPrenom, role: 'formateur',
              signature: extraSig || formateursMap[fid].signature || null
            });
          } catch {
            formateursList.push({
              nom: fNom, prenom: fPrenom, role: 'formateur',
              signature: formateursMap[fid].signature || null
            });
          }
        }
      }
      
      const presencesData = {
        date,
        centre: stagiaires[0]?.centre_formation || 'Atlantique',
        periode,
        formateurs: formateursList,
        stagiaires: Object.values(stagiaireMap).filter(s => s.matin || s.apres_midi)
      };

      const pdf = await generatePresenceCollectivePDF(presencesData);
      downloadPDF(pdf, `${date}_presence_collective.pdf`);
      toast.success('PDF généré avec succès');
    } catch (error) {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const toggleStagiaire = (userId) => {
    setSelectedStagiaires(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const availableStagiaires = stagiaires.filter(s => !alreadyValidated.includes(s.user_id));
  const selectedCount = availableStagiaires.filter(s => selectedStagiaires[s.user_id]).length;
  const allSelected = availableStagiaires.length > 0 && selectedCount === availableStagiaires.length;

  const toggleAll = () => {
    const newSelected = { ...selectedStagiaires };
    const newValue = !allSelected;
    availableStagiaires.forEach(s => { newSelected[s.user_id] = newValue; });
    setSelectedStagiaires(newSelected);
  };

  const statutOptions = [
    { value: 'present', label: 'Présent', icon: CheckCircle, color: 'text-[#8DC63F] bg-green-50 border-green-200' },
    { value: 'absent', label: 'Absent', icon: XCircle, color: 'text-red-500 bg-red-50 border-red-200' },
    { value: 'retard', label: 'Retard', icon: Clock, color: 'text-orange-500 bg-orange-50 border-orange-200' },
    { value: 'justifie', label: 'Absence justifiée', icon: FileText, color: 'text-blue-500 bg-blue-50 border-blue-200' },
    { value: 'conges', label: 'Congés', icon: Coffee, color: 'text-purple-500 bg-purple-50 border-purple-200' }
  ];

  return (
    <div className="space-y-6" data-testid="presence-page">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Feuille de Présence</h1>
        <p className="text-slate-600">Marquer la présence des stagiaires</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
                data-testid="input-date"
              />
            </div>

            {/* Sélection Matin / Après-midi / Jour entier */}
            <div className="space-y-2">
              <Label>Demi-journée</Label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setDemiJournee('matin')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg border-2 transition-all ${
                    demiJournee === 'matin'
                      ? 'bg-orange-100 border-orange-400 text-orange-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-orange-300'
                  }`}
                  data-testid="btn-matin"
                >
                  <Sun className="h-5 w-5" />
                  <span className="font-medium">Matin</span>
                </button>
                <button
                  type="button"
                  onClick={() => setDemiJournee('apres_midi')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg border-2 transition-all ${
                    demiJournee === 'apres_midi'
                      ? 'bg-indigo-100 border-indigo-400 text-indigo-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300'
                  }`}
                  data-testid="btn-apres-midi"
                >
                  <Sunset className="h-5 w-5" />
                  <span className="font-medium">Après-midi</span>
                </button>
                <button
                  type="button"
                  onClick={() => setDemiJournee('journee')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg border-2 transition-all ${
                    demiJournee === 'journee'
                      ? 'bg-emerald-100 border-emerald-400 text-emerald-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300'
                  }`}
                  data-testid="btn-jour-entier"
                  title="Enregistre Matin + Après-midi en une seule action"
                >
                  <SunMedium className="h-5 w-5" />
                  <span className="font-medium">Jour entier</span>
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {/* Bouton tout sélectionner / désélectionner */}
            {availableStagiaires.length > 0 && (
              <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                <button
                  type="button"
                  onClick={toggleAll}
                  className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#8DC63F] transition-colors"
                  data-testid="toggle-all-checkbox"
                >
                  {allSelected ? (
                    <CheckSquare className="h-5 w-5 text-[#8DC63F]" />
                  ) : (
                    <Square className="h-5 w-5 text-slate-400" />
                  )}
                  {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                </button>
                <span className="text-sm text-slate-500">
                  {selectedCount} / {availableStagiaires.length} sélectionné(s)
                </span>
              </div>
            )}

            {stagiaires.map((stagiaire) => {
              const currentStatus = presences[stagiaire.user_id] || 'present';
              const isAlreadyValidated = alreadyValidated.includes(stagiaire.user_id);
              const isSelected = !!selectedStagiaires[stagiaire.user_id];
              return (
                <div
                  key={stagiaire.user_id}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    isAlreadyValidated ? 'border-blue-300 bg-blue-50 opacity-70' : 
                    isSelected ? 'border-[#8DC63F] bg-green-50/30' : 'border-slate-200'
                  }`}
                  data-testid={`presence-row-${stagiaire.user_id}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center space-x-3">
                      {/* Checkbox de sélection */}
                      {!isAlreadyValidated && (
                        <button
                          type="button"
                          onClick={() => toggleStagiaire(stagiaire.user_id)}
                          className="flex-shrink-0"
                          data-testid={`checkbox-${stagiaire.user_id}`}
                        >
                          {isSelected ? (
                            <CheckSquare className="h-5 w-5 text-[#8DC63F]" />
                          ) : (
                            <Square className="h-5 w-5 text-slate-400 hover:text-slate-600" />
                          )}
                        </button>
                      )}
                      <div>
                        <p className="font-semibold text-[#0F172A]">
                          {stagiaire.user_info?.prenom} {stagiaire.user_info?.nom}
                          {isAlreadyValidated && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                              Déjà validé
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-slate-600">
                          {stagiaire.centre_formation} - {stagiaire.statut}
                        </p>
                      </div>
                      {/* Signature du stagiaire */}
                      {signatures[stagiaire.user_id] && (
                        <div className="ml-3 border border-slate-200 rounded bg-white p-1" title="Signature du stagiaire">
                          <img
                            src={signatures[stagiaire.user_id]}
                            alt="Signature"
                            className="h-8 w-auto max-w-[100px] object-contain"
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      {isAlreadyValidated ? (
                        <span className="text-sm text-blue-600 italic">Présence déjà enregistrée par un autre formateur</span>
                      ) : (
                        statutOptions.map((option) => {
                          const Icon = option.icon;
                          const isStatusSelected = currentStatus === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => setPresences({ ...presences, [stagiaire.user_id]: option.value })}
                              className={`
                                flex items-center space-x-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border-2 transition-all text-xs sm:text-sm
                                ${isStatusSelected ? option.color + ' font-semibold' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}
                              `}
                              data-testid={`status-button-${option.value}-${stagiaire.user_id}`}
                            >
                              <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                              <span className="hidden sm:inline">{option.label}</span>
                              <span className="sm:hidden">{option.value === 'justifie' ? 'Abs.Just.' : option.value === 'conges' ? 'Congé' : option.value === 'present' ? 'P' : option.value === 'absent' ? 'Abs' : 'Ret.'}</span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-between items-center flex-wrap gap-4">
          <div className="flex gap-2 items-center">
            {/* Signature formateur */}
            {formateurSignature && (
              <div className="flex items-center gap-2 border border-slate-200 rounded-lg p-2 bg-white" data-testid="formateur-signature-preview">
                <span className="text-xs text-slate-500 font-medium">Signature {user?.role === 'tuteur' ? 'tuteur' : 'formateur'}:</span>
                <img src={formateurSignature} alt="Signature formateur" className="h-8 w-auto max-w-[120px] object-contain" />
              </div>
            )}
            <Button
              onClick={handleGeneratePDF}
              variant="outline"
              data-testid="generate-pdf-button"
            >
              <Download className="h-4 w-4 mr-2" />
              Télécharger PDF
            </Button>
          </div>
          <Button
            type="submit"
            className="bg-[#8DC63F] hover:bg-[#7db336] text-white px-8"
            disabled={selectedCount === 0}
            data-testid="submit-presence-button"
          >
            {selectedCount > 0 
              ? `Enregistrer et envoyer récap direction (${selectedCount})`
              : 'Sélectionnez des stagiaires'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Presence;
